/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useReducer, useCallback} from "react"
import {Tabs, Spin} from "antd"
import {useTranslation} from "react-i18next"
import {cloneDeep, isEmpty, omit} from "lodash"
import {BasePopup} from "components/popup"
import debounce from "debounce-promise"
import {Search} from "components/inputs/Search"
import {handleError} from "helpers"
import {getUserPhotoUrl} from "helpers/user"
import {studentStaffContactsServiceV3} from "services"
import {AdvisorBody} from "./AdvisorBody"
import styles from "./AdvisorsPopup.module.css"
import {BaseDepartmentId} from "types/departments"
import {TabHeader} from "components/Tab"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {NonCampusAdvisors} from "./NonCampusAdvisors"
import {CollegeSlug} from "types/clients"
import {useModel} from "hooks"
import {StudentStaffContactRole} from "types/contacts"

type Staff = {
    staffProfileId: number
    firstName: string
    middleName: string
    lastName: string
    photo: string
    studentStaffContactId: number
    studentStaffContactRoleId: number
    studentProfileId: number
    isDefault: number
    add?: boolean
    remove?: boolean
    edit?: boolean
}

type State = {
    oldStaffs: Staff[]
    staffs: Staff[]
    isLoading: boolean
    activeTabKey: string
    search: string
    nonCampusSelectedAdvisors: Staff[]
}

type Props = {
    profileIds: number[]
    departmentId?: number
    studentStaffContactRoleIds?: number[]
    campusIds?: any
    isVisible: boolean
    close: (reload?: boolean) => void
    advisorTitle: AdvisorTitle
    addMultiple?: boolean
    type?: string
}

type Module = {
    id: number
    studentStaffContactRoleId: number
    name: string
}

const modules: Module[] = [
    {
        id: BaseDepartmentId.Admissions,
        studentStaffContactRoleId: StudentStaffContactRole.AdmissionAdvisor,
        name: "Admissions"
    },
    {
        id: BaseDepartmentId.FinancialAid,
        studentStaffContactRoleId: StudentStaffContactRole.FinAidAdvisor,
        name: "Financial Aid"
    },
    {
        id: BaseDepartmentId.Academics,
        studentStaffContactRoleId: StudentStaffContactRole.AcademicAdvisor,
        name: "Academics"
    },
    {
        id: BaseDepartmentId.StudentServices,
        studentStaffContactRoleId: StudentStaffContactRole.StudentServicesAdvisor,
        name: "Student Services"
    }
]

const studentStaffContactsModules = [
    {
        id: BaseDepartmentId.Admissions,
        studentStaffContactRoleId: StudentStaffContactRole.EnrollmentSpecialist,
        name: "Enrollment Specialist"
    },
    {
        id: BaseDepartmentId.FinancialAid,
        studentStaffContactRoleId: StudentStaffContactRole.FSA,
        name: "Financial Service Advisor (FSA)"
    }
]

export enum AdvisorTitle {
    finAidAdvisors = "finAidAdvisors",
    admissionAdvisors = "admissionAdvisors",
    advisors = "advisors"
}

export function AdvisorsPopup(props: Props) {
    function reducer(state, action) {
        return {...state, ...action}
    }
    const model = useModel()
    const {slug} = model

    const {isVisible, advisorTitle, campusIds, profileIds, type, studentStaffContactRoleIds} = props
    const allModules = modules.flatMap((baseModule) => {
        const group = [baseModule]

        if ([CollegeSlug.Northstar, CollegeSlug.HHS].indexOf(slug as CollegeSlug) >= 0) {
            const extras = studentStaffContactsModules.filter((m) => m.id === baseModule.id)
            group.push(...extras)
        }

        return group
    })

    const filteredModules = studentStaffContactRoleIds
        ? allModules.filter((mod) => studentStaffContactRoleIds.includes(mod.studentStaffContactRoleId))
        : allModules
    const initialState: State = {
        oldStaffs: [],
        staffs: [],
        isLoading: false,
        nonCampusSelectedAdvisors: [],
        activeTabKey:
            props.departmentId && !props.studentStaffContactRoleIds
                ? `${props.departmentId}`
                : filteredModules.length > 0
                ? String(filteredModules[0].studentStaffContactRoleId)
                : "0",
        search: ""
    }

    const [state, dispatch] = useReducer(reducer, initialState)
    const {oldStaffs, staffs, isLoading, activeTabKey, search, nonCampusSelectedAdvisors} = state
    const {t} = useTranslation(["common"])

    const selectedModule = filteredModules.find((mod) => mod.studentStaffContactRoleId === +activeTabKey)
    useEffect(() => {
        if (profileIds?.length) {
            getStaffs()
        }
    }, [profileIds, activeTabKey])

    const getStaffs = async (search = "") => {
        dispatch({isLoading: true})
        try {
            const filter = {
                ...(profileIds?.length && {profileIds}),
                departmentId: props.departmentId || selectedModule.id,
                studentStaffContactRoleId: +selectedModule.studentStaffContactRoleId,
                search,
                campusIds: isEmpty(campusIds) ? undefined : campusIds,
                ...(type && type !== "" && {byMultipleActions: true})
            }
            const [{data: selectedAdvisors}, {data}] = await Promise.all([
                studentStaffContactsServiceV3.getStaffs({
                    range: {pageSize: UNLIMITED_PAGE_SIZE, page: 1},
                    filter: {...omit(filter, "campusIds"), isSelected: true, ignoreCampusIds: filter.campusIds}
                }),
                studentStaffContactsServiceV3.getStaffs({
                    range: {pageSize: 20, page: 1},
                    filter
                })
            ])
            const allAdvisorProfileIds = data.map((item) => item.staffProfileId)
            const nonCampusSelectedAdvisors = selectedAdvisors.filter(
                (item) => !allAdvisorProfileIds.includes(item.staffProfileId)
            )
            const staffs = data.map((staff) => ({
                ...staff,
                photo: staff.photo ? getUserPhotoUrl(staff.photo, 32) : "/image/DefaultAvatar.png"
            }))
            dispatch({staffs, nonCampusSelectedAdvisors, oldStaffs: cloneDeep(staffs)})
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onClose = (reload?: boolean) => {
        if (!reload) dispatch({staffs: cloneDeep(oldStaffs)})
        props.close(reload)
    }

    const onSearchStaff = (search) => {
        dispatch({search})
        debounceGetStaff(search)
    }

    const changeStaffs = (staffs) => {
        dispatch({staffs})
    }

    const debounceGetStaff = useCallback(debounce(getStaffs, 500), [activeTabKey, profileIds])

    const onChangeTab = (activeTabKey) => {
        dispatch({activeTabKey, search: ""})
    }

    const renderBody = (departmentId = props.departmentId, studentStaffContactRoleId = null) => {
        if (isLoading) {
            return (
                <div className={styles.spinContainer}>
                    <Spin />
                </div>
            )
        }
        return (
            <div>
                <AdvisorBody
                    {...props}
                    departmentId={departmentId}
                    studentStaffContactRoleId={studentStaffContactRoleId || +activeTabKey}
                    staffs={staffs}
                    changeStaffs={changeStaffs}
                    getStaffs={getStaffs}
                    onClose={onClose}
                    inSearchMode={!!search}
                />
            </div>
        )
    }

    const renderDepartmentTabs = () => {
        return (
            <Tabs className="fullwidth" activeKey={activeTabKey} onChange={onChangeTab}>
                {filteredModules?.map((module) => {
                    const isActive = module.studentStaffContactRoleId === +activeTabKey

                    return (
                        <Tabs.TabPane tab={<TabHeader title={module.name} />} key={module.studentStaffContactRoleId}>
                            {isActive && (
                                <NonCampusAdvisors
                                    nonCampusSelectedAdvisors={nonCampusSelectedAdvisors}
                                    isLoading={isLoading}
                                    getStaffs={getStaffs}
                                />
                            )}
                            <Search value={search} onChange={onSearchStaff} placeholder={t("action.search")} />
                            {isActive && renderBody(module.id, module.studentStaffContactRoleId)}
                        </Tabs.TabPane>
                    )
                })}
            </Tabs>
        )
    }
    const departmentName = modules.find(({id}) => id === props.departmentId)?.name || ""
    return (
        <BasePopup isShow={isVisible} onClose={onClose} leftIcon="EDIT_LINE" leftIconColor="#fff" width="70vw">
            <Tabs className="fullwidth" activeKey="1">
                <Tabs.TabPane tab={<TabHeader title={t(`common:advisorsPopup.advisors`, {departmentName})} />} key="1">
                    {props.departmentId && !props.studentStaffContactRoleIds ? (
                        <>
                            <NonCampusAdvisors
                                nonCampusSelectedAdvisors={nonCampusSelectedAdvisors}
                                isLoading={isLoading}
                                getStaffs={getStaffs}
                            />
                            <Search value={search} onChange={onSearchStaff} placeholder={t("action.search")} />
                            {renderBody()}
                        </>
                    ) : (
                        renderDepartmentTabs()
                    )}
                </Tabs.TabPane>
            </Tabs>
        </BasePopup>
    )
}
